import { useEffect, useMemo } from "react";
import { IS_MIX_CATALOG } from "@/config/browserBoard";
import { fetchRegisteredBoards } from "@/features/settings/api/addedBoards";
import {
  type AddedBoard,
  useMixStore,
} from "@/features/settings/stores/mixStore";
import { createMediator } from "@/shared/lib/componentHierarchy";
import { useFromMixCatalog } from "@/shared/lib/mixReturn";
import { useHierarchyLifetime } from "@/shared/ui/component-hierarchy/hooks";

type SyncEvent = {
  readonly type: "added-boards/registered";
  readonly boards: readonly AddedBoard[];
};

/** 登録一覧の確認成功時だけ利用不可状態を更新し、通信失敗では保存を保持する。 */
export function AddedBoardsSync(): null {
  const fromMixCatalog = useFromMixCatalog();
  const hasAddedBoards = useMixStore((state) => state.addedBoards.length > 0);
  const mediator = useMemo(
    () =>
      createMediator<null, SyncEvent, readonly AddedBoard[]>(
        null,
        (state, event) => ({
          status: "handled",
          state,
          effects: [event.boards],
        }),
        {
          runEffect: (boards) =>
            useMixStore.getState().syncRegisteredBoards(boards),
        },
      ),
    [],
  );
  useHierarchyLifetime(mediator);
  useEffect(() => {
    if (!IS_MIX_CATALOG && !fromMixCatalog && !hasAddedBoards) return;
    const controller = new AbortController();
    void (async (): Promise<void> => {
      try {
        const boards = await fetchRegisteredBoards(controller.signal);
        controller.signal.throwIfAborted();
        mediator.dispatch({ type: "added-boards/registered", boards });
      } catch {
        // A failed API request cannot establish that a saved board was removed.
      }
    })();
    return (): void => controller.abort();
  }, [mediator, hasAddedBoards, fromMixCatalog]);
  return null;
}
